import { useCallback, useEffect, useState } from "react";
import { Layers, RefreshCw, Play, Pause, X as XIcon, AlertTriangle } from "lucide-react";
import {
  api,
  type BacklogJob,
  type BacklogEstimate,
} from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

/** Backlog actions experience. User-triggerable batch jobs that
 *  operate on historical data: Gmail backfill, Gmail cleanup,
 *  reanalyze, etc. Each action card surfaces a one-tap "Estimate
 *  cost" button before any destructive or expensive work runs.
 *
 *  Cost-conscious by design (per the audit's ship-beyond-scaffolding
 *  rule + the user's own framing): the estimate phase counts items
 *  + projects token / time / dollar cost, the user reads it, then
 *  approves or doesn't. The actual job runs in the backlog worker
 *  at lower priority than realtime ingestion. */
export function Backlog() {
  const [actions, setActions] = useState<Array<{ kind: string; title: string; description: string; requiresBulkConfirmation: boolean }> | null>(null);
  const [jobs, setJobs] = useState<BacklogJob[]>([]);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    api.backlog().then((d) => setJobs(d.jobs)).catch(() => undefined);
    api.backlogActions().then((d) => setActions(d.actions)).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 5_000);
    return () => window.clearInterval(t);
  }, [load]);

  const runReanalyze = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.reanalyze(days);
      setMsg(`Queued ${r.queued} captures for reanalyse.`);
      load();
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Backlog
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          User-triggerable batch jobs over historical data. Each action shows a cost estimate before it runs.
        </p>
      </header>

      <TeachingBanner id="backlog_overview" />

      {actions && actions.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            Available actions
          </h3>
          <div className="grid gap-3">
            {actions.map((a) => (
              <ActionCard key={a.kind} action={a} onChanged={load} jobs={jobs} />
            ))}
          </div>
        </section>
      )}

      <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-5 space-y-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white shadow-md">
            <Layers className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Reanalyze</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Re-runs the 5-stage pipeline on every capture in the window.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            Last
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 7)))}
              className="w-16 rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
            />
            days
          </label>
          <button
            type="button"
            onClick={runReanalyze}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 transition-colors disabled:opacity-50"
          >
            <Play className="w-3.5 h-3.5" />
            {busy ? "Queueing…" : "Run reanalyze"}
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-200 dark:hover:bg-slate-700"
            aria-label="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {msg && <div className="text-xs text-slate-600 dark:text-slate-300">{msg}</div>}
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
          Recent jobs
        </h3>
        {jobs.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 italic">No jobs yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {jobs.map((j) => (
              <JobRow key={j.id} job={j} onChanged={load} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ActionCard({
  action,
  onChanged,
  jobs,
}: {
  action: { kind: string; title: string; description: string; requiresBulkConfirmation: boolean };
  onChanged: () => void;
  jobs: BacklogJob[];
}) {
  const [phase, setPhase] = useState<"idle" | "estimating" | "estimate-ready" | "running">("idle");
  const [error, setError] = useState<string | null>(null);
  const [estimateJobId, setEstimateJobId] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<BacklogEstimate | null>(null);

  // If there's already an in-flight job of this kind for the user,
  // surface its progress here instead of letting the user kick off
  // a duplicate.
  const inFlight = jobs.find(
    (j) =>
      j.kind === action.kind &&
      ["estimating", "awaiting_approval", "queued", "running", "paused", "awaiting_confirmation"].includes(j.status),
  );

  const startEstimate = async () => {
    setError(null);
    setPhase("estimating");
    try {
      const r = await api.backlogEstimate(action.kind);
      setEstimateJobId(r.jobId);
      // Poll for the estimate to land (it's synchronous in v1 but the
      // SPA path is async-friendly for future LLM-backed estimates).
      const poll = async (): Promise<void> => {
        const job = await api.backlogJob(r.jobId);
        if (job.job.status === "awaiting_approval" && job.job.estimate_json) {
          setEstimate(JSON.parse(job.job.estimate_json) as BacklogEstimate);
          setPhase("estimate-ready");
          return;
        }
        if (job.job.status === "error") {
          setError("Estimate failed.");
          setPhase("idle");
          return;
        }
        await new Promise((r) => setTimeout(r, 750));
        return poll();
      };
      await poll();
    } catch (err) {
      setError((err as Error).message);
      setPhase("idle");
    }
  };

  const approve = async () => {
    if (!estimateJobId) return;
    try {
      await api.backlogApprove(estimateJobId);
      setPhase("running");
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const cancel = async () => {
    if (!estimateJobId) return;
    await api.backlogCancel(estimateJobId).catch(() => undefined);
    setEstimateJobId(null);
    setEstimate(null);
    setPhase("idle");
    onChanged();
  };

  return (
    <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-5 space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{action.title}</h3>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">{action.description}</p>
      </div>

      {inFlight ? (
        <InFlightPanel job={inFlight} onChanged={onChanged} />
      ) : phase === "idle" ? (
        <button
          type="button"
          onClick={startEstimate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2"
        >
          <Play className="w-3.5 h-3.5" />
          Estimate cost
        </button>
      ) : phase === "estimating" ? (
        <div className="text-xs text-slate-600 dark:text-slate-300">Estimating…</div>
      ) : phase === "estimate-ready" && estimate ? (
        <div className="space-y-2">
          <div className="rounded-lg bg-amber-50/60 dark:bg-amber-900/20 border border-amber-300/60 dark:border-amber-700/40 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <div className="font-semibold mb-0.5 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Confirm before running
            </div>
            <div>{estimate.summary}</div>
            {action.requiresBulkConfirmation && (
              <div className="mt-1 text-[11px] opacity-80">
                You'll review + bulk-confirm any destructive actions before they run.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={approve}
              className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-3 py-2"
            >
              Run now
            </button>
            <button
              type="button"
              onClick={cancel}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-500"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-emerald-700 dark:text-emerald-300">Started — see jobs below.</div>
      )}

      {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
    </div>
  );
}

function InFlightPanel({ job, onChanged }: { job: BacklogJob; onChanged: () => void }) {
  const pct = Math.round((job.progress ?? 0) * 100);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${statusColor(job.status)}`}>
          {job.status.replace(/_/g, " ")}
        </span>
        <span className="text-xs tabular-nums text-slate-700 dark:text-slate-200">
          {job.processed}/{job.total}
        </span>
        {job.errors > 0 && (
          <span className="text-xs text-rose-600 dark:text-rose-300">{job.errors} err</span>
        )}
      </div>
      {job.status === "running" && (
        <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full bg-sky-500 transition-all"
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
      )}
      <div className="flex items-center gap-2">
        {job.status === "running" && (
          <button
            type="button"
            onClick={async () => { await api.backlogPause(job.id); onChanged(); }}
            className="inline-flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200 hover:underline"
          >
            <Pause className="w-3 h-3" />
            Pause
          </button>
        )}
        {job.status === "paused" && (
          <button
            type="button"
            onClick={async () => { await api.backlogResume(job.id); onChanged(); }}
            className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
          >
            <Play className="w-3 h-3" />
            Resume
          </button>
        )}
        {["queued", "running", "paused", "awaiting_approval", "awaiting_confirmation"].includes(job.status) && (
          <button
            type="button"
            onClick={async () => { await api.backlogCancel(job.id); onChanged(); }}
            className="inline-flex items-center gap-1 text-xs text-rose-600 dark:text-rose-300 hover:underline"
          >
            <XIcon className="w-3 h-3" />
            Cancel
          </button>
        )}
        {job.status === "awaiting_confirmation" && (
          <a
            href="/cleanup/gmail"
            className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 dark:text-orange-300 hover:underline"
          >
            Review and confirm →
          </a>
        )}
      </div>
    </div>
  );
}

function JobRow({ job, onChanged }: { job: BacklogJob; onChanged: () => void }) {
  // (onChanged unused for the bare row — header chips show but the
  // action panel under each ActionCard owns lifecycle controls. Kept
  // in the signature for symmetry + future row-level affordances.)
  void onChanged;
  return (
    <li className="glass rounded-lg px-3 py-2 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0">
        {job.kind}
      </span>
      <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${statusColor(job.status)}`}>
        {job.status.replace(/_/g, " ")}
      </span>
      <span className="text-sm tabular-nums text-slate-700 dark:text-slate-200 flex-1">
        {job.processed}/{job.total} {job.errors > 0 ? `(${job.errors} err)` : ""}
      </span>
      <span className="text-[11px] text-slate-500 dark:text-slate-400">
        {new Date(job.created_at).toLocaleString()}
      </span>
    </li>
  );
}

function statusColor(s: string): string {
  switch (s) {
    case "done": return "bg-emerald-200 dark:bg-emerald-700/40 text-emerald-800 dark:text-emerald-200";
    case "running": return "bg-sky-200 dark:bg-sky-700/40 text-sky-800 dark:text-sky-200";
    case "paused": return "bg-amber-200 dark:bg-amber-700/40 text-amber-800 dark:text-amber-200";
    case "awaiting_approval": return "bg-amber-200 dark:bg-amber-700/40 text-amber-800 dark:text-amber-200";
    case "awaiting_confirmation": return "bg-orange-200 dark:bg-orange-700/40 text-orange-800 dark:text-orange-200";
    case "error": return "bg-rose-200 dark:bg-rose-700/40 text-rose-800 dark:text-rose-200";
    case "cancelled": return "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400";
    default: return "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200";
  }
}
